import { useEffect, useRef, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'

/**
 * List state pre-filled from `seed` (a customer phone/email), re-seeded every
 * time the panel switches to another shipment. The detail sidebar swaps
 * `shipmentId` in place without remounting this panel as you click through
 * rows, so a one-shot seed would strand the first row's value on every later
 * row. Manual edits within a row survive until the next switch.
 */
export function useSeededList(
  shipmentId: string | undefined,
  seed: string,
): [string[], Dispatch<SetStateAction<string[]>>] {
  const [values, setValues] = useState<string[]>([])
  const seededRef = useRef(false)
  const shownShipmentRef = useRef(shipmentId)

  useEffect(() => {
    if (shownShipmentRef.current !== shipmentId) {
      shownShipmentRef.current = shipmentId
      seededRef.current = false
      setValues([])
    }
    if (!seededRef.current && seed) {
      setValues([seed])
      seededRef.current = true
    }
  }, [shipmentId, seed])

  return [values, setValues]
}

/**
 * Run `onReset` whenever the panel switches to another shipment (it never
 * remounts — see {@link useSeededList}). Skips the initial mount, so a fresh
 * draft is only cleared on an actual row change, not on first open.
 */
export function useResetOnShipmentChange(
  shipmentId: string | undefined,
  onReset: () => void,
) {
  const shownShipmentRef = useRef(shipmentId)
  useEffect(() => {
    if (shownShipmentRef.current === shipmentId) return
    shownShipmentRef.current = shipmentId
    onReset()
  }, [shipmentId, onReset])
}
